import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell, X, ClipboardList, FileText, Gift, Check } from 'lucide-react';
import axios from 'axios';

export default function NotificationBell() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/student/notifications`, { headers, withCredentials: true });
        setNotifications(res.data.notifications || []);
        setUnread(res.data.unread_count || 0);
      } catch {}
    };
    if (token) fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, [API, token]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/student/notifications/${id}/read`, {}, { headers, withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/student/notifications/read-all`, {}, { headers, withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };

  const getIcon = (type) => {
    if (type === 'assignment') return <ClipboardList size={14} className="text-[#E63946]" />;
    if (type === 'worksheet') return <FileText size={14} className="text-[#8338EC]" />;
    if (type === 'referral') return <Gift size={14} className="text-[#FF6B35]" />;
    return <Bell size={14} className="text-[#1B4F8A]" />;
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div ref={ref} className="relative">
      <button data-testid="notification-bell" onClick={() => setOpen(!open)}
        className="w-10 h-10 rounded-full flex items-center justify-center bg-white border-2 relative"
        style={{ borderColor: unread > 0 ? '#FF6B35' : '#E5DACE' }}>
        <Bell size={18} color={unread > 0 ? '#FF6B35' : '#1B4F8A'} />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#E63946] text-white text-[10px] font-bold flex items-center justify-center animate-bounce">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown"
          className="absolute right-0 top-12 w-80 bg-white border-2 border-[#E5DACE] rounded-2xl shadow-lg z-50 overflow-hidden"
          style={{ maxHeight: '400px' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5DACE] bg-[#F8F4EF]">
            <span className="text-sm font-bold text-[#1B4F8A]">Notifications</span>
            <div className="flex gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs font-bold text-[#8338EC] hover:underline" data-testid="mark-all-read">
                  <Check size={12} className="inline mr-0.5" />Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)}><X size={16} className="text-slate-400" /></button>
            </div>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '340px' }}>
            {notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell size={24} className="mx-auto text-[#E5DACE] mb-2" />
                <p className="text-xs text-slate-400 font-semibold">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 15).map(n => (
                <button key={n.id} data-testid={`notification-${n.id}`}
                  onClick={() => { if (!n.read) markRead(n.id); if (n.link) { navigate(n.link); setOpen(false); } }}
                  className={`w-full text-left px-4 py-3 border-b border-[#F0E6D8] hover:bg-[#FFFBF5] transition-colors flex gap-3 items-start
                    ${!n.read ? 'bg-[#FFF8F0]' : ''}`}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: !n.read ? '#FFF0E5' : '#F0E6D8' }}>
                    {getIcon(n.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${!n.read ? 'font-bold text-[#1B4F8A]' : 'font-semibold text-slate-500'} truncate`}>
                      {n.title}
                    </p>
                    <p className="text-[11px] text-slate-400 truncate">{n.message}</p>
                    <p className="text-[10px] text-slate-300 mt-0.5">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#FF6B35] shrink-0 mt-2" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
